<template>
  <v-container class="page">
    <v-overlay :value="overlay">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>
    <v-card
      v-if="is_page_table"
      width="100%"
      style="border-radius:10px;"
      class="pa-3"
      v-show="!overlay"
    >
      <v-card-title class="title font-weight-light">
        Email Template Management
        <v-spacer />
        <v-text-field
          v-model="searchData"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
        />
      </v-card-title>
      <v-card-text>
        <div class="d-flex">
          <v-btn
            color="#00ACDC"
            dark
            class="mb-2 ml-auto"
            @click="handleClickCreateNew"
          >
            Create New Email Template
          </v-btn>
        </div>
        <v-data-table
          :headers="headers"
          :items="email_templates"
          class="font-weight-light"
          :loading="loading_email"
          loading-text="Loading..."
          :footer-props="footerProps"
          @update:options="updateEmailTemplate"
          :server-items-length="email_templates_total"
          :options.sync="paginationOptions"
        >
          <template #[`item.no`]=" { item }">
            {{ ((paginationOptions.page - 1) * paginationOptions.itemsPerPage) + email_templates.indexOf(item) + 1 }}
          </template>
          <template #[`item.type`]="{ item }">
            {{ getTypeName(item.type) }}
          </template>
          <template #[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at, 'D MMM YYYY HH:mm') }}
          </template>
          <template #[`item.updated_at`]="{ item }">
            {{ formatDate(item.updated_at, 'D MMM YYYY HH:mm') }}
          </template>
          <template #[`item.action`]="{ item }">
            <v-icon
              small
              class="mr-2"
              color="green"
              @click="handleClickEdit(item)"
            >
              mdi-pencil
            </v-icon>
            <v-icon
              small
              color="red"
              @click="handleClickDelete(item)"
            >
              mdi-delete
            </v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-card
      v-if="is_page_form"
      class="mx-auto"
      style="border-radius:10px;"
    >
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text>
        <v-form
          ref="form"
          model="valid"
          lazy-validation
        >
          <v-row>
            <v-col>
              <v-card-subtitle class="px-0 pt-0">
                Subject
              </v-card-subtitle>
              <v-text-field
                v-model="subject"
                :rules="[(v) => !!v || 'Subject cannot be blank']"
                :counter="255"
                required
                outlined
                dense
              />
            </v-col>
            <v-col>
              <v-card-subtitle class="px-0 pt-0">
                Type
              </v-card-subtitle>
              <v-select
                v-model="type"
                :items="filter_email_template_types"
                item-text="value"
                item-value="key"
                :rules="[(v) => !!v || 'Please select at least one type']"
                required
                outlined
                dense
              />
            </v-col>
          </v-row>
          <v-card-subtitle class="px-0 pt-0">
            <span>Body</span>
            <v-btn
              class="float-right"
              @click="handleLoadDefault"
            >
              Load default template
            </v-btn>
          </v-card-subtitle>
          <tiptap-vuetify
            ref="tiptap"
            v-model="body"
            :extensions="extensions"
            :toolbar-attributes="{ color: '#00ACDC' }"
            min-height="300"
            class="pt-1"
            placeholder="Start Typing Here"
          />
          <span
            v-if="bodyEmpty"
            class="red--text caption"
          >Body cannot be blank</span>
        </v-form>
      </v-card-text>
      <v-card-actions class="px-4 mb-3">
        <v-spacer />
        <v-btn
          @click="handleClickBack"
          type="submit"
          color="error"
        >
          Back
        </v-btn>
        <v-btn
          @click="handleClickSubmit"
          type="submit"
          color="success"
          :loading="loadingUpdate"
          :disabled="loadingUpdate"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog
      v-model="dialog"
      persistent
      max-width="600"
    >
      <v-card
        dark
        color="red"
      >
        <v-card-title>
          <span>
            <v-icon>mdi-alert-circle</v-icon> Are you sure to want to
            delete this email template?
          </span>
        </v-card-title>
        <v-card-text>
          You will delete this email template permanently.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="dialog = false"
          >
            No
          </v-btn>
          <v-btn
            text
            @click="handleConfirmDelete()"
            :loading="loadingUpdate"
            :disabled="loadingUpdate"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="replace"
      persistent
      max-width="600"
    >
      <v-card
        dark
        color="red"
      >
        <v-card-title>
          <span>
            <v-icon>mdi-alert-circle</v-icon> Are you sure to want to
            load default template?
          </span>
        </v-card-title>
        <v-card-text>
          Your current email template body is not empty, you will lose your current email template body.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="replace = false"
          >
            No
          </v-btn>
          <v-btn
            text
            @click="handleClickReplace()"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment';

import {
TiptapVuetify,
Heading,
Bold,
Italic,
Strike,
Underline,
Code,
Paragraph,
BulletList,
OrderedList,
ListItem,
Link,
Blockquote,
HardBreak,
HorizontalRule,
History,
Image,
Table,
TableCell,
TableHeader,
TableRow
} from 'tiptap-vuetify'

export default {
  components: { TiptapVuetify },
  data: () => ({
    page: 'table',
    extensions: [
      History,
      Table,
      TableCell,
      TableHeader,
      TableRow,
      Blockquote,
      Link,
      Underline,
      Strike,
      Italic,
      ListItem,
      BulletList,
      OrderedList,
      Image,
      [
        Heading,
        {
          options: {
            levels: [1, 2, 3]
          }
        }
      ],
      Bold,
      Link,
      Code,
      HorizontalRule,
      Paragraph,
      HardBreak
    ],
    headers: [
      { text: "No", value: "no", sortable: false },
      {
        text: 'Type',
        value: 'type',
      },
      {
        text: 'Subject',
        value: 'subject',
      },
      {
        text: 'Created At',
        value: 'created_at',
      },
      {
        text: 'Updated At',
        value: 'updated_at',
      },
      {
        text: 'Action',
        value: 'action',
        sortable: false,
      },
    ],
    types: [
      {
        key: 'user_registration_notification',
        value: 'User Registration Notification',
        contents: [
          'name',
          'email',
          'user_approve_link',
          'user_decline_link'
        ],
        default: `
          <p>Hello,</p>
          <p>We have received a new registration request. Please find the details of the user below:</p>
          <p></p>
          <p>Name: [Name]</p>
          <p>Email: [Email]</p>
          <p></p>
          <p>Please click one of the links below to either approve or decline the request</p>
          <p><a href='[Booking Approve Link]'>Approve</a> or <a href='[Booking Decline Link]'>Decline</a></p>
        `
      },
      {
        key: 'user_registration_approved',
        value: 'User Registration Approved',
        contents: [
          'username',
          'user_first_name',
          'user_last_name',
          'user_email',
          'user_type',
          'user_password',
          'licence_type',
          'licence_no',
          'licence_expiry_date',
          'licence_issuing_state'
        ],
        default: `
          <p>Hello,</p>
          <p>You receive this email because your email address has been registered to Intellitrac Car Pooling.</p>
          <p>This is your temporary credentials:</p>
          <p></p>
          <p>Username: [Username]</p>
          <p>Password: [Password]</p>
          <p></p>
          <p>For security reason, please login to the <a href='${process.env.VUE_APP_CARPOOLING_URL}'>Carpooling website</a> and change the password as soon as possible.</p>
          <p></p>
          <p>Thanks,</p>
          <p>Intellitrac Car Pooling Team</p>
        `
      },
      {
        key: 'user_registration_declined',
        value: 'User Registration Declined',
        contents: [
          'username',
          'user_first_name',
          'user_last_name',
          'user_email',
          'user_type',
          'user_licence_type',
          'licence_no',
          'licence_expiry_date',
          'licence_issuing_state',
        ],
        default: `
          <p>Hello,</p>
          <p></p>
          <p>We were unable to approve your registration for a reason.</p>
          <p>Please contact admin if you have any additional questions.</p>
          <p></p>
          <td>Intellitrac Car Pooling Team</td>
        `
      },
      {
        key: 'booking_out_of_working_hours_request',
        value: 'Booking Out Of Working Hours Request',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_approve_link',
          'booking_decline_link',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Hello,</p>
          <p>[Booking Passenger Name] has requested for out of working hours booking:</p>
          <p></p>
          <p>From: [Booking Origin]</p>
          <p>To: [Booking Destination]</p>
          <p></p>
          <p>[Vehicle Group]</p>
          <p>[Vehicle Name]</p>
          <p>[Booking Passenger Count] Passenger(s)</p>
          <p></p>
          <p>Booking Type: [Booking Type]</p>
          <p>Booking Date: [Booking Period]</p>
          <p></p>
          <p>Please click one of the links below to either approve or decline the request</p>
          <p><a href='[Booking Approve Link]'>Approve</a> or <a href='[Booking Decline Link]'>Decline</a></p>
        `
      },
      {
        key: 'overnight_booking_request',
        value: 'Overnight Booking Request',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_approve_link',
          'booking_decline_link',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Hello,</p>
          <p>[Booking Passenger Name] has requested for overnight booking:</p>
          <p></p>
          <p>From: [Booking Origin]</p>
          <p>To: [Booking Destination]</p>
          <p></p>
          <p>[Vehicle Group]</p>
          <p>[Vehicle Name]</p>
          <p>[Booking Passenger Count] Passenger(s)</p>
          <p></p>
          <p>Booking Type: [Booking Type]</p>
          <p>Booking Date: [Booking Period]</p>
          <p></p>
          <p>Please click one of the links below to either approve or decline the request</p>
          <p><a href='[Booking Approve Link]'>Approve</a> or <a href='[Booking Decline Link]'>Decline</a></p>
        `
      },
      {
        key: 'overnight_booking_request_approved',
        value: 'Overnight Booking Request Approved',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Hello,</p>
          <p>I have approved your request for overnight booking:</p>
          <p></p>
          <p>From: [Booking Destination]</p>
          <p>To: [Booking Origin]</p>
          <p></p>
          <p>[Vehicle Group]</p>
          <p>[Vehicle Name]</p>
          <p>[Booking Passenger Count] Passenger(s)</p>
          <p></p>
          <p>Booking Type: [Booking Type]</p>
          <p>Booking Date: [Booking Period]</p>
          <p></p>
          <p>Regards,</p>
          <p></p>
          <p>Admin</p>
        `
      },
      {
        key: 'overnight_booking_request_declined',
        value: 'Overnight Booking Request Declined',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Hello,</p>
          <p>Unfortunately I have declined your request for overnight booking:</p>
          <p></p>
          <p>From: [Booking Destination]</p>
          <p>To: [Booking Origin]</p>
          <p></p>
          <p>[Vehicle Group]</p>
          <p>[Vehicle Name]</p>
          <p>[Booking Passenger Count] Passenger(s)</p>
          <p></p>
          <p>Booking Type: [Booking Type]</p>
          <p>Booking Date: [Booking Period]</p>
          <p></p>
          <p>Regards,</p>
          <p></p>
          <p>Admin</p>
        `
      },
      {
        key: 'booking_recurring_request',
        value: 'Booking Recurring Request',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_recurring_schedules',
          'booking_approve_link',
          'booking_decline_link',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Hello,</p>
          <p>[Booking Passenger Name] has requested for recurring booking:</p>
          <p></p>
          <p>From: [Booking Origin]</p>
          <p>To: [Booking Destination]</p>
          <p></p>
          <p>[Vehicle Group]</p>
          <p>[Vehicle Name]</p>
          <p>[Booking Passenger Count] Passenger(s)</p>
          <p></p>
          <p>Booking Type: [Booking Type]</p>
          <p>Booking Date: [Booking Period]</p>
          <p>Recurring Schedules</p>
          <p>[Booking Recurring Schedules]</p>
          <p></p>
          <p>Please click one of the links below to either approve or decline the request</p>
          <p><a href='[Booking Approve Link]'>Approve</a> or <a href='[Booking Decline Link]'>Decline</a></p>
        `
      },
      {
        key: 'booking_recurring_request_approved',
        value: 'Booking Recurring Request Approved',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'vehicle_parking_location',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_recurring_schedules',
          'cabinet_name',
          'cabinet_key_number',
        ],
        default: `
          <p>Hello,</p>
          <p>I have approved your request for recurring booking:</p>
          <p></p>
          <p>From: [Booking Destination]</p>
          <p>To: [Booking Origin]</p>
          <p></p>
          <p>[Vehicle Group]</p>
          <p>[Vehicle Name]</p>
          <p>[Booking Passenger Count] Passenger(s)</p>
          <p></p>
          <p>Booking Type: [Booking Type]</p>
          <p>Booking Date: [Booking Period]</p>
          <p>Recurring Schedules</p>
          <p>[Booking Recurring Schedules]</p>
          <p></p>
          <p>Regards,</p>
          <p></p>
          <p>Admin</p>`
      },
      {
        key: 'booking_recurring_request_declined',
        value: 'Booking Recurring Request Declined',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_recurring_schedules',
          'booking_declined_reason',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Hello,</p>
          <p>Unfortunately I have declined your request for recurring booking:</p>
          <p></p>
          <p>From: [Booking Destination]</p>
          <p>To: [Booking Origin]</p>
          <p></p>
          <p>[Vehicle Group]</p>
          <p>[Vehicle Name]</p>
          <p>[Booking Passenger Count] Passenger(s)</p>
          <p></p>
          <p>Booking Type: [Booking Type]</p>
          <p>Booking Date: [Booking Period]</p>
          <p>Recurring Schedules</p>
          <p>[Booking Recurring Schedules]</p>
          <p></p>
          <p>Reason: [Booking Declined Reason]</p>
          <p></p>
          <p>Regards,</p>
          <p></p>
          <p>Admin</p>
        `
      },
      {
        key: 'booking_sharing_ride_request',
        value: 'Booking Sharing Ride Request',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_approve_link',
          'booking_decline_link',
        ],
        default: `
          <p>Hello,</p>
          <p>[Booking Passenger Name] has requested for join booking:</p>
          <p></p>
          <p>From: [Booking Origin]</p>
          <p>To: [Booking Destination]</p>
          <p></p>
          <p>[Vehicle Group]</p>
          <p>[Vehicle Name]</p>
          <p>[Booking Passenger Count] Passenger(s)</p>
          <p></p>
          <p>Booking Type: [Booking Type]</p>
          <p>Booking Date: [Booking Period]</p>
          <p></p>
          <p>Please click one of the links below to either approve or decline the request</p>
          <p><a href='[Booking Approve Link]'>Approve</a> or <a href='[Booking Decline Link]'>Decline</a></p>
        `
      },
      {
        key: 'booking_sharing_ride_request_approved',
        value: 'Booking Sharing Ride Request Approved',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'vehicle_parking_location',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
        ],
        default: `
          <p>Hello,</p>
          <p>I have approved your request for join booking:</p>
          <p></p>
          <p>From: [Booking Destination]</p>
          <p>To: [Booking Origin]</p>
          <p></p>
          <p>[Vehicle Group]</p>
          <p>[Vehicle Name]</p>
          <p>[Booking Passenger Count] Passenger(s)</p>
          <p></p>
          <p>Booking Type: [Booking Type]</p>
          <p>Booking Date: [Booking Period]</p>
          <p></p>
          <p>Regards,</p>
          <p></p>
          <p>Admin</p>
        `
      },
      {
        key: 'booking_sharing_ride_request_declined',
        value: 'Booking Sharing Ride Request Declined',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_declined_reason'
        ],
        default: `
          <p>Hello,</p>
          <p>Unfortunately I have declined your request for join booking:</p>
          <p></p>
          <p>From: [Booking Destination]</p>
          <p>To: [Booking Origin]</p>
          <p></p>
          <p>[Vehicle Group]</p>
          <p>[Vehicle Name]</p>
          <p>[Booking Passenger Count] Passenger(s)</p>
          <p></p>
          <p>Booking Type: [Booking Type]</p>
          <p>Booking Date: [Booking Period]</p>
          <p>Recurring Schedules</p>
          <p>[Booking Recurring Schedules]</p>
          <p></p>
          <p>Reason: [Booking Declined Reason]</p>
          <p></p>
          <p>Regards,</p>
          <p></p>
          <p>Admin</p>
        `
      },
      {
        key: 'late_returning_key',
        value: 'Late Returning Key',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Dear [Booking Passenger Name],</p>
          <p></p>
          <p>We regret to inform you that the vehicle you reserved is currently unavailable due to the previous renter not returning the keys.</p>
          <p>We are working to resolve this issue promptly.</p>
          <p></p>
          <p>For further assistance or more information, please contact our administration.</p>
          <p>We apologize for the inconvenience and appreciate your understanding.</p>
          <p></p>
          <p>Best Regards,<p>
          <p>Intellitrac Car Pooling Team</p>
        `
      },
      {
        key: 'swap_vehicle',
        value: 'Swap Vehicle',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'old_vehicle_id',
          'old_vehicle_name',
          'old_vehicle_plate',
          'old_vehicle_make',
          'old_vehicle_model',
          'old_vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period'
        ],
        default: `
          <p>Hello,</p>
          <p>We want to inform you that your booking with information below:</p>
          <p></p>
          <p>From: [Booking Origin]</p>
          <p>To: [Booking Destination]</p>
          <p></p>
          <p>[Old Vehicle Group]</p>
          <p>[Old Vehicle Name]</p>
          <p>[Booking Passenger Count] Passenger(s)</p>
          <p></p>
          <p>Booking Type: [Booking Type]</p>
          <p>Booking Date: [Booking Period]</p>
          <p></p>
          <p>We have to change your current vehicle to <b>[Vehicle Name]</b> due to service.
          <p></p>
          <p>Regards,</p>
          <p></p>
          <p>Admin</p>
        `
      },
      {
        key: 'user_licence_expired',
        value: 'Licence Update Request Email',
        contents: [
          'user_name',
          'licence_expiry_date',
          'admin_name',
          'council_name',
          'email_address',
          'phone_number',
        ],
        default: `
          <p>Dear [User Name],</p>
          <p>Your licence is due to expiry [Licence Expiry Date] Please email a copy of your licence to Carpooling Admin.</p>
          <p>Please note you will be unable to book a pool car without a valid licence recorded.</p>
          <p></p>
          <p>Best Regards,</p>
          <p></p>
          <p>[Admin Name]</p>
          <p>Council Carpooling Administrator</p>
          <p>[Council Name]</p>
          <p>[Email Address]</p>
          <p>[Phone Number]</p>
        `
      },
    ],
    contents: [
      {
        key: 'username',
        value: '[Username]'
      },
      {
        key: 'user_first_name',
        value: '[User First Name]'
      },
      {
        key: 'user_last_name',
        value: '[User Last Name]'
      },
      {
        key: 'user_type',
        value: '[User Type]'
      },
      {
        key: 'user_password',
        value: '[User Password]'
      },
      {
        key: 'licence_type',
        value: '[Licence Type]'
      },
      {
        key: 'licence_no',
        value: '[Licence No]'
      },
      {
        key: 'licence_expiry_date',
        value: '[Licence Expiry Date]'
      },
      {
        key: 'licence_issuing_state',
        value: '[Licence Issuing State]'
      },
      {
        key: 'vehicle_id',
        value: '[Vehicle ID]'
      },
      {
        key: 'vehicle_name',
        value: '[Vehicle Name]'
      },
      {
        key: 'vehicle_make',
        value: '[Vehicle Make]'
      },
      {
        key: 'vehicle_model',
        value: '[Vehicle Model]'
      },
      {
        key: 'vehicle_plate',
        value: '[Vehicle Plate]'
      },
      {
        key: 'vehicle_group',
        value: '[Vehicle Group]'
      },
      {
        key: 'vehicle_parking_location',
        value: '[Vehicle Parking Location]'
      },
      {
        key: 'old_vehicle_id',
        value: '[Old Vehicle ID]'
      },
      {
        key: 'old_vehicle_name',
        value: '[Old Vehicle Name]'
      },
      {
        key: 'old_vehicle_make',
        value: '[Old Vehicle Make]'
      },
      {
        key: 'old_vehicle_model',
        value: '[Old Vehicle Model]'
      },
      {
        key: 'old_vehicle_plate',
        value: '[Old Vehicle Plate]'
      },
      {
        key: 'old_vehicle_group',
        value: '[Old Vehicle Group]'
      },
      {
        key: 'booking_type',
        value: '[Booking Type]'
      },
      {
        key: 'booking_passenger_name',
        value: '[Booking Passenger Name]'
      },
      {
        key: 'booking_passenger_email',
        value: '[Booking Passenger Email]'
      },
      {
        key: 'booking_passenger_count',
        value: '[Booking Passenger Count]'
      },
      {
        key: 'booking_origin',
        value: '[Booking Origin]'
      },
      {
        key: 'booking_destination',
        value: '[Booking Destination]'
      },
      {
        key: 'booking_start_date',
        value: '[Booking Start Date]'
      },
      {
        key: 'booking_end_date',
        value: '[Booking End Date]'
      },
      {
        key: 'booking_period',
        value: '[Booking Period]'
      },
      {
        key: 'booking_recurring_schedules',
        value: '[Booking Recurring Schedules]'
      },
      {
        key: 'booking_approve_link',
        value: '[Booking Approve Link]'
      },
      {
        key: 'booking_decline_link',
        value: '[Booking Decline Link]'
      },
      {
        key: 'booking_declined_reason',
        value: '[Booking Declined Reason]'
      },
      {
        key: 'user_name',
        value: '[User Name]'
      },
      {
        key: 'admin_name',
        value: '[Admin Name]'
      },
      {
        key: 'council_name',
        value: '[Council Name]'
      },
      {
        key: 'email_address',
        value: '[Email Address]'
      },
      {
        key: 'phone_number',
        value: '[Phone Number]'
      },
      {
        key: 'cost_code',
        value: '[Booking Cost Code]'
      },
      {
        key: 'cabinet_name',
        value: '[Cabinet Name]'
      },
      {
        key: 'cabinet_key_number',
        value: '[Cabinet Key Number]'
      },
      {
        key: 'user_approve_link',
        value: '[User Approve Link]'
      },
      {
        key: 'user_decline_link',
        value: '[User Decline Link]'
      },
    ],
    valid: true,
    dialog: false,
    replace: false,
    id: '',
    type: '',
    subject: '',
    body: '',
    searchData: '',
    searchDataTimeout: null,
    loadingUpdate: false,
    loader: null,
    overlay: false,
    footerProps: {
      itemsPerPageOptions: [2, 3, 5, 10, 20, 50, 100, 200, 500]
    },
    paginationOptions: {
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: []
    }
  }),
  created() {
    this.$store.commit('CLEAR_EMAIL_TEMPLATES');
    this.$store.commit('CLEAR_EMAIL_TEMPLATES_TOTAL');
    this.$store.commit('CLEAR_USED_EMAIL_TEMPLATE_TYPES');
    this.$store.dispatch('LOAD_EMAIL_TEMPLATES', this.paginationOptions);
    this.$store.dispatch('LOAD_EMAIL_TEMPLATES_TOTAL');
    this.$store.dispatch('LOAD_USED_EMAIL_TEMPLATE_TYPES');
    this.overlay = true;
  },  
  mounted() {
    if (this.isDarwin) {
      const typeIndex = this.types.findIndex(t => t.key === 'booking_out_of_working_hours_request');
      this.types[typeIndex].contents.push('cost_code');
      this.types[typeIndex].default = `
        <p>Hello,</p>
        <p>[Booking Passenger Name] has requested for out of working hours booking:</p>
        <p></p>
        <p>From: [Booking Origin]</p>
        <p>To: [Booking Destination]</p>
        <p></p>
        <p>[Vehicle Group]</p>
        <p>[Vehicle Name]</p>
        <p>[Booking Passenger Count] Passenger(s)</p>
        <p></p>
        <p>Booking Type: [Booking Type]</p>
        <p>Booking Date: [Booking Period]</p>
        <p>Booking Cost Code: [Booking Cost Code]</p>
        <p></p>
        <p>Please click one of the links below to either approve or decline the request</p>
        <p><a href='[Booking Approve Link]'>Approve</a> or <a href='[Booking Decline Link]'>Decline</a></p>
      `;
    }
  },
  computed: {
    is_page_table() {
      return this.page === 'table';
    },
    is_page_form() {
      return this.page === 'form';
    },
    title() {
      return this.id === '' ? 'Create New Email Template' : 'Edit Email Template';
    },
    loading() {
      return this.$store.getters['loading'];
    },
    loading_total() {
      return this.$store.getters['loading_total'];
    },
    loading_type() {
      return this.$store.getters['loading_type'];
    },
    loading_email() {
      return this.loading || this.loading_total || this.loading_type;
    },
    query() {
      return this.$store.getters['query'];
    },
    email_templates() {
      return this.$store.getters['email_templates'];
    },
    email_templates_total() {
      return this.$store.getters['email_templates_total'];
    },
    used_email_template_types() {
      return this.$store.getters['used_email_template_types'];
    },
    filter_email_template_types() {
      const filtered_types = this.types.filter(type => {
        const matchItem = this.used_email_template_types.find(t => t === type.key);
        if (this.type !== '') {
          if (this.type === type.key) return true;
        }
        return typeof matchItem === 'undefined';
      });
      return filtered_types;
    },
    bodyEmpty() {
      return this.body == '' || this.body == '<p></p>';
    },
    isDarwin() {
      return this.$store.state.clientid === "ED73T6OILP78";
    },
  },
  watch: {
    type() {
      this.$nextTick(() => {
        if (this.type === '') return
        const toolbar = this.$refs.tiptap.$children[0].$el.querySelector('.tiptap-vuetify-editor__toolbar .v-toolbar__content');
        toolbar.querySelector('.select-option__content').disabled = false;
        this.updateContentList();
      });
    },
    loader() {
      const l = this.loader
      this[l] = !this[l]

      setTimeout(() => (this[l] = false), 3000)

      this.loader = null
    },
    overlay(val) {
      val && setTimeout(() => {
        this.overlay = false
      }, 2e3)
    },
    searchData(query) {
      clearTimeout(this.searchDataTimeout);
      this.searchDataTimeout = setTimeout(() => {
        this.paginationOptions.page     = 1;
        this.paginationOptions.sortBy   = [];
        this.paginationOptions.sortDesc = [];

        this.$store.commit('CLEAR_QUERY');
        this.$store.commit('CLEAR_EMAIL_TEMPLATES');
        this.$store.commit('CLEAR_EMAIL_TEMPLATES_TOTAL');
        this.$store.commit('CLEAR_USED_EMAIL_TEMPLATE_TYPES');
        this.$store.commit('SET_QUERY', query);
        this.$store.dispatch('LOAD_EMAIL_TEMPLATES', this.paginationOptions);
        this.$store.dispatch('LOAD_EMAIL_TEMPLATES_TOTAL');
        this.$store.dispatch('LOAD_USED_EMAIL_TEMPLATE_TYPES');
      }, 1e3);
    }
  },
  methods: {
    formatDate(date, format = 'YYYY-MM-DD HH:mm:ss') {
      const selectedDate = moment(date);
      if (selectedDate.isValid()) {
        return selectedDate.format(format);
      }
      return date;
    },
    handleClickCreateNew() {
      const currentPage = 'form';
      this.page = currentPage;
      this.createSelectOption();
    },
    handleClickBack() {
      const currentPage = 'table';
      this.page = currentPage;
      this.id = '';
      this.type = '';
      this.subject = '';
      this.body = '';
    },
    handleConfirmDelete() {
      this.dialog = false;
      this.loadingUpdate = true;
      this.loader = this.loadingUpdate;
      this.$store
      .dispatch('DELETE_EMAIL_TEMPLATE', this.id)
      .then(() => {
        this.loadingUpdate = false;
        this.loader = this.loadingUpdate;
        this.$swal({
          icon: "success",
          title: "Success delete email template",
        });
        this.$store.commit('CLEAR_EMAIL_TEMPLATES_TOTAL');
        this.$store.commit('CLEAR_EMAIL_TEMPLATES');
        this.$store.commit('CLEAR_USED_EMAIL_TEMPLATE_TYPES');
        this.$store.dispatch('LOAD_EMAIL_TEMPLATES_TOTAL');
        this.$store.dispatch('LOAD_USED_EMAIL_TEMPLATE_TYPES');
        this.$store
        .dispatch('LOAD_EMAIL_TEMPLATES', this.paginationOptions)
        .then(() => {
          this.id = '';
        });
      });
    },
    handleClickDelete(item) {
      this.dialog = true;
      this.id = item.id;
    },
    handleClickEdit(item) {
      const currentPage = 'form';
      this.page = currentPage;
      this.id = item.id;
      this.type = item.type;
      this.subject = item.subject;
      this.body = item.body;
      this.createSelectOption();
    },
    handleClickSubmit() {
      if (this.$refs.form.validate() && this.body != '') {
        let action = 'CREATE';
        const payload = {
          type: this.type,
          subject: this.subject,
          body: this.body,
          active: 1,
        };
        let successMsg = this.id === '' ? 'Success create email template.' : 'Success update email template';

        if (this.id !== '') {
          action = 'UPDATE';
          payload.id = this.id;
        }

        this.loadingUpdate = true;
        this.loader = this.loadingUpdate;

        this.$store
          .dispatch(`${action}_EMAIL_TEMPLATE`, payload)
          .then(() => {
            this.loadingUpdate = false;
            this.loader = this.loadingUpdate;
            this.$swal({
              icon: "success",
              title: successMsg,
            });
            this.$store.commit('CLEAR_USED_EMAIL_TEMPLATE_TYPES');
            this.$store.commit('CLEAR_EMAIL_TEMPLATES_TOTAL');
            this.$store.commit('CLEAR_EMAIL_TEMPLATES');
            this.$store.dispatch('LOAD_USED_EMAIL_TEMPLATE_TYPES');
            this.$store.dispatch('LOAD_EMAIL_TEMPLATES_TOTAL');
            this.$store
              .dispatch('LOAD_EMAIL_TEMPLATES', this.paginationOptions)
              .then(() => {
                this.handleClickBack();
              });
          })
      }
    },
    createSelectOption() {
      this.$nextTick(() => {
        this.$refs.form.validate();

        const vm = this;
        const toolbar = this.$refs.tiptap.$children[0].$el.querySelector('.tiptap-vuetify-editor__toolbar .v-toolbar__content');

        const label = document.createElement('label');
        label.style.marginLeft = '1rem';

        const span = document.createElement('span');
        span.innerHTML = 'Insert Content: ';
        label.appendChild(span);

        const selectOption = document.createElement('select');
        selectOption.classList.add('select-option__content');
        selectOption.style.background = '#fff';

        if (vm.type === '') selectOption.disabled = true;

        label.appendChild(selectOption);
        toolbar.appendChild(label);

        for (const content of this.contents) {
          const option = document.createElement('option');
          option.value = content.key;
          option.innerHTML = content.value;
          selectOption.appendChild(option);
        }

        selectOption.addEventListener('change', (e) => {
          const emptyBody = vm.body.trim() === '' || vm.body.trim() === '<p></p>';
          const content = vm.contents.find(content => content.key === e.target.value);
          let range, sel = window.getSelection();
          if (sel.rangeCount) {
            range = sel.getRangeAt(0);
            if (emptyBody || range.commonAncestorContainer.parentNode.parentNode.classList.contains('ProseMirror')) {
              if (emptyBody) {
                vm.body = `<p class="">${content.value}</p>`;
              } else {
                range.insertNode(document.createTextNode(content.value));
              }
            }
          }
        });
      });
    },
    updateContentList() {
      this.$nextTick(() => {
        const type = this.types.find(type => type.key === this.type);
        const toolbar = this.$refs.tiptap.$children[0].$el.querySelector('.tiptap-vuetify-editor__toolbar .v-toolbar__content');
        const contentOptions = toolbar.querySelectorAll('.select-option__content option');

        contentOptions.forEach((item) => {
          if (item.style) {
            item.style.display = 'none';
          }
        });

        let selected = '';

        for (const content of type.contents) {
          const selectedContent = Array.from(contentOptions).find(option => option.value === content);
          if (selected == '') {
            selected = selectedContent;
            toolbar.querySelector('.select-option__content').value = content
          }
          if (selectedContent) {
            selectedContent.style.display = '';
          }
        }
      });
    },
    updateEmailTemplate(paginationOptions) {
      this.paginationOptions = paginationOptions;
      this.$store.commit('CLEAR_EMAIL_TEMPLATES');
      this.$store.commit('CLEAR_EMAIL_TEMPLATES_TOTAL');
      this.$store.commit('CLEAR_USED_EMAIL_TEMPLATE_TYPES');
      this.$store.dispatch('LOAD_EMAIL_TEMPLATES', this.paginationOptions);
      this.$store.dispatch('LOAD_EMAIL_TEMPLATES_TOTAL');
      this.$store.dispatch('LOAD_USED_EMAIL_TEMPLATE_TYPES');
    },
    getTypeName(type) {
      const selectedType = this.types.find(t => t.key == type);
      return selectedType.value;
    },
    handleLoadDefault() {
      if (!this.bodyEmpty) {
        this.replace = true
      } else {
        this.handleClickReplace()
      }
    },
    handleClickReplace() {
      const selectedType = this.types.find(type => type.key === this.type)
      this.body = selectedType.default
      this.replace = false
    }
  }
}
</script>
